import { Suspense, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type ForwardedRef, type PropsWithChildren } from 'react';
import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Text, Html, Billboard } from '@react-three/drei';
import * as THREE from 'three';
import type { PublicStation, PublicChallenge } from '@cdf/api-client-react';
import {
  BOOTHS,
  ROOMS,
  REGISTRATION_DESK,
  RESTROOM_BLOCK,
  HOTEL_RECEPTION,
  FLOOR_PLATES,
  FLOOR_BOUNDS,
  AISLES,
  STRUCTURAL_WALLS,
  BOOTH_COLORS,
  CAMERA_PRESETS,
  VIRTUAL_SCREEN_MOUNTS,
  DEFAULT_VIRTUAL_SCREEN_CONTENT,
  type BoothDef,
  type BoothFacing,
  type RoomDef,
  type DoorSide,
  type CameraPreset,
  type VirtualScreenContent,
  type WallSegment
} from './conference-twin-data';
import { checkWebGLCapability } from '@/lib/webgl-capability';
import { parseExhibitorName, parseStandNumber } from '@/components/hunt/algim-program';
import { VirtualRoomScreen } from './virtual-room-screen';
import type { PanDirection } from '@/components/ui/zoom-pan-controls';
import { ListIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DesktopTwinPanel } from '@/components/twin/desktop-twin-panel';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import type { EventTwinItem } from '@cdf/api-client-react';
import { WallPlaqueMesh } from './wall-plaque-mesh';

// ── Types ──────────────────────────────────────────────────────────────

export type HuntOverlayStatus = 'live' | 'upcoming' | 'ended' | 'unscheduled';

export interface ConferenceSelectedItem {
  kind: 'booth' | 'room' | 'registration';
  stand?: number;
  roomId?: string;
  stationId?: string;
  stationName: string;
  stationPoints: number;
  challenges: PublicChallenge[];
  /** Schedule metadata when selected via hunt overlay / active session. */
  roomLabel?: string | null;
  presenter?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  scheduleStatus?: HuntOverlayStatus;
  sessionTitle?: string | null;
}

/** Active session room marker for the treasure-hunt overlay. */
export interface HuntRoomBeacon {
  /** Stable key (usually schedule room name). */
  id: string;
  roomId?: string;
  x: number;
  y: number;
  z: number;
  status: HuntOverlayStatus;
  roomLabel: string;
  shortLabel: string;
  stationId?: string;
  stationName: string;
  stationPoints: number;
  presenter?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  sessionTitle?: string | null;
  challenges: PublicChallenge[];
  liveCount: number;
  /** True when every session quiz on this beacon is completed. */
  completed?: boolean;
  completedCount?: number;
}

interface ConferenceTwinSceneProps extends PropsWithChildren {
  stations: PublicStation[];
  challenges: PublicChallenge[];
  onSelect: (item: ConferenceSelectedItem | null) => void;
  selectedId: string | null;
  cameraPreset: CameraPreset;
  /** When true, render animated beacons for `huntBeacons`. */
  huntOverlayEnabled?: boolean;
  huntBeacons?: HuntRoomBeacon[];
  /** Timeline-derived content keyed by presentation room ID. */
  virtualScreenContent?: Readonly<Record<string, VirtualScreenContent>>;
  wallItems?: EventTwinItem[];
  selectedWallItemId?: string | null;
  onWallItemSelect?: (item: EventTwinItem) => void;
}

export interface ConferenceTwinCameraController {
  zoomIn: () => void;
  zoomOut: () => void;
  pan: (direction: PanDirection) => void;
  reset: () => void;
}

type ZoomTarget = [number, number, number];

function ClickToZoom({ onZoom }: { onZoom: (point: THREE.Vector3) => void }) {
  const { camera, gl } = useThree();

  useEffect(() => {
    const canvas = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const point = new THREE.Vector3();
    let lastTouchTap: { time: number; x: number; y: number } | null = null;

    const zoomFromEvent = (event: MouseEvent | PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      pointer.set(
        ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
        -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      if (raycaster.ray.intersectPlane(floor, point)) onZoom(point.clone());
    };

    const handleDoubleClick = (event: MouseEvent) => {
      event.preventDefault();
      zoomFromEvent(event);
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') return;
      const now = event.timeStamp;
      const isDoubleTap = lastTouchTap && now - lastTouchTap.time < 350 &&
        Math.hypot(event.clientX - lastTouchTap.x, event.clientY - lastTouchTap.y) < 24;
      lastTouchTap = { time: now, x: event.clientX, y: event.clientY };
      if (isDoubleTap) {
        zoomFromEvent(event);
        lastTouchTap = null;
      }
    };

    canvas.addEventListener('dblclick', handleDoubleClick);
    canvas.addEventListener('pointerdown', handlePointerDown);
    return () => {
      canvas.removeEventListener('dblclick', handleDoubleClick);
      canvas.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [camera, gl, onZoom]);

  return null;
}

// ── Wall builder ────────────────────────────────────────────────────────

function createWallGeometry(
  x1: number,
  z1: number,
  x2: number,
  z2: number,
  height: number,
  thickness = 0.3
): THREE.BufferGeometry {
  const dx = x2 - x1;
  const dz = z2 - z1;
  const length = Math.max(Math.sqrt(dx * dx + dz * dz), 0.01);
  const angle = Math.atan2(dz, dx);
  const midX = (x1 + x2) / 2;
  const midZ = (z1 + z2) / 2;

  const geo = new THREE.BoxGeometry(length, height, thickness);
  const mesh = new THREE.Mesh(geo);
  mesh.position.set(midX, height / 2, midZ);
  mesh.rotation.y = -angle;
  mesh.updateMatrix();
  return geo.clone().applyMatrix4(mesh.matrix);
}

function wallSegmentsForRoom(room: RoomDef): Array<[number, number, number, number]> {
  const halfW = room.width / 2;
  const halfD = room.depth / 2;
  const left = room.x - halfW;
  const right = room.x + halfW;
  const north = room.z - halfD;
  const south = room.z + halfD;
  const doorGap = Math.min(4, room.width * 0.35, room.depth * 0.35);

  const segs: Array<[number, number, number, number]> = [];

  const addSide = (side: DoorSide, full: [number, number, number, number], withDoor: boolean) => {
    if (!withDoor) {
      segs.push(full);
      return;
    }
    // Split the wall around a centred door opening
    if (side === 'north' || side === 'south') {
      const z = full[1];
      const mid = room.x;
      segs.push([left, z, mid - doorGap / 2, z]);
      segs.push([mid + doorGap / 2, z, right, z]);
    } else {
      const x = full[0];
      const mid = room.z;
      segs.push([x, north, x, mid - doorGap / 2]);
      segs.push([x, mid + doorGap / 2, x, south]);
    }
  };

  addSide('north', [left, north, right, north], room.doorSide === 'north');
  addSide('south', [left, south, right, south], room.doorSide === 'south');
  addSide('east', [right, north, right, south], room.doorSide === 'east');
  addSide('west', [left, north, left, south], room.doorSide === 'west');

  return segs;
}

// ── Room component ──────────────────────────────────────────────────────

function Chair({ x, z, facing = 0 }: { x: number; z: number; facing?: number }) {
  return (
    <group position={[x, 0, z]} rotation={[0, facing, 0]}>
      <mesh position={[0, 0.42, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.62, 0.13, 0.62]} />
        <meshStandardMaterial color="#334155" roughness={0.58} />
      </mesh>
      <mesh position={[0, 0.78, 0.25]} castShadow receiveShadow>
        <boxGeometry args={[0.62, 0.62, 0.1]} />
        <meshStandardMaterial color="#475569" roughness={0.62} />
      </mesh>
      {[-0.23, 0.23].flatMap((offsetX) => [-0.23, 0.23].map((offsetZ) => (
        <mesh key={`${offsetX}-${offsetZ}`} position={[offsetX, 0.2, offsetZ]} castShadow>
          <boxGeometry args={[0.07, 0.4, 0.07]} />
          <meshStandardMaterial color="#1e293b" metalness={0.32} roughness={0.5} />
        </mesh>
      )))}
    </group>
  );
}

function ConferenceRoomFurniture({ room }: { room: RoomDef }) {
  if (room.id === 'expo-hall') return null;

  const isBoardroom = room.id === 'swanston' || room.id === 'degraves';
  const tableRows = room.id === 'plenary' ? 3 : isBoardroom ? 1 : 2;
  const tableColumns = room.id === 'plenary' ? 2 : isBoardroom ? 1 : room.id === 'elizabeth' ? 2 : 1;
  const tableWidth = isBoardroom ? room.width * 0.52 : room.id === 'plenary' ? 3.4 : 3.2;
  const tableDepth = isBoardroom ? room.depth * 0.34 : 1.35;
  const rowGap = room.id === 'plenary' ? 4.4 : 3.7;
  const columnGap = room.id === 'plenary' ? 5.2 : 4.4;
  const tableColor = isBoardroom ? '#7c5a40' : '#8b6b52';

  const tables = Array.from({ length: tableRows * tableColumns }, (_, index) => {
    const row = Math.floor(index / tableColumns);
    const column = index % tableColumns;
    return {
      id: `${room.id}-table-${index + 1}`,
      x: room.x + (column - (tableColumns - 1) / 2) * columnGap,
      z: room.z + (row - (tableRows - 1) / 2) * rowGap,
    };
  });

  return (
    <group aria-label={`${room.name} tables and chairs`}>
      {tables.map((table) => (
        <group key={table.id} position={[table.x, 0, table.z]}>
          <mesh position={[0, 0.78, 0]} castShadow receiveShadow>
            <boxGeometry args={[tableWidth, 0.16, tableDepth]} />
            <meshStandardMaterial color={tableColor} roughness={0.48} metalness={0.08} />
          </mesh>
          {[-1, 1].flatMap((xDirection) => [-1, 1].map((zDirection) => (
            <mesh key={`${xDirection}-${zDirection}`} position={[xDirection * (tableWidth / 2 - 0.24), 0.38, zDirection * (tableDepth / 2 - 0.2)]} castShadow>
              <boxGeometry args={[0.12, 0.76, 0.12]} />
              <meshStandardMaterial color="#3f2d24" roughness={0.52} />
            </mesh>
          )))}
          {[-1, 1].flatMap((side) => {
            const chairCount = isBoardroom ? 3 : 2;
            return Array.from({ length: chairCount }, (_, chairIndex) => {
              const spread = (chairIndex - (chairCount - 1) / 2) * 1.1;
              return (
                <Chair
                  key={`${side}-${chairIndex}`}
                  x={spread}
                  z={side * (tableDepth / 2 + 0.58)}
                  facing={side > 0 ? Math.PI : 0}
                />
              );
            });
          })}
          {isBoardroom && [-1, 1].map((side) => (
            <Chair
              key={`end-${side}`}
              x={side * (tableWidth / 2 + 0.58)}
              z={0}
              facing={side > 0 ? -Math.PI / 2 : Math.PI / 2}
            />
          ))}
        </group>
      ))}
    </group>
  );
}

function RoomMesh({
  room,
  onSelect,
  isSelected,
  stationId,
  challenges,
  virtualScreenContent,
  hasHuntBeacon,
}: {
  room: RoomDef;
  onSelect: (item: ConferenceSelectedItem) => void;
  isSelected: boolean;
  stationId?: string;
  challenges: PublicChallenge[];
  virtualScreenContent?: VirtualScreenContent;
  hasHuntBeacon: boolean;
}) {
  const pulseRef = useRef<THREE.Mesh>(null);
  const screenMount = VIRTUAL_SCREEN_MOUNTS.find((mount) => mount.roomId === room.id);

  useFrame((state) => {
    if (pulseRef.current && isSelected) {
      const t = state.clock.elapsedTime;
      pulseRef.current.scale.setScalar(1 + Math.sin(t * 3) * 0.04);
    }
  });

  const wallGeometries = useMemo(() => {
    return wallSegmentsForRoom(room).map(([x1, z1, x2, z2]) =>
      createWallGeometry(x1, z1, x2, z2, room.height)
    );
  }, [room]);

  return (
    <group>
      <mesh
        position={[room.x, 0.05, room.z]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onSelect({
            kind: 'room',
            roomId: room.id,
            stationId,
            stationName: room.name,
            stationPoints: 0,
            challenges,
            roomLabel: room.name,
          });
        }}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'default';
        }}
      >
        <planeGeometry args={[room.width, room.depth]} />
        <meshStandardMaterial
          color={room.floorColor}
          roughness={0.9}
          emissive={isSelected ? room.wallColor : '#000000'}
          emissiveIntensity={isSelected ? 0.15 : 0}
        />
      </mesh>

      {isSelected && (
        <mesh
          ref={pulseRef}
          position={[room.x, 0.08, room.z]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <ringGeometry
            args={[
              Math.max(room.width, room.depth) / 2,
              Math.max(room.width, room.depth) / 2 + 0.8,
              32
            ]}
          />
          <meshStandardMaterial
            color={room.wallColor}
            emissive={room.wallColor}
            emissiveIntensity={0.5}
            transparent
            opacity={0.6}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}

      {wallGeometries.map((geo, i) => (
        <mesh key={i} geometry={geo} castShadow receiveShadow>
          <meshStandardMaterial
            color={room.wallColor}
            transparent
            opacity={0.78}
            roughness={0.7}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}

      <ConferenceRoomFurniture room={room} />

      {screenMount && (
        <VirtualRoomScreen
          mount={screenMount}
          content={
            virtualScreenContent ?? { ...DEFAULT_VIRTUAL_SCREEN_CONTENT, roomId: room.id }
          }
          isSelected={isSelected}
          onSelect={() => {
            const screenContent =
              virtualScreenContent ?? { ...DEFAULT_VIRTUAL_SCREEN_CONTENT, roomId: room.id };
            onSelect({
              kind: 'room',
              roomId: room.id,
              stationId,
              stationName: screenContent.sessionTitle || room.name,
              stationPoints: 0,
              challenges,
              roomLabel: room.name,
              presenter: screenContent.presenter,
              startTime: screenContent.startTime,
              endTime: screenContent.endTime,
              scheduleStatus: screenContent.status === 'idle' ? 'unscheduled' : screenContent.status,
              sessionTitle: screenContent.sessionTitle,
            });
          }}
        />
      )}

      {!hasHuntBeacon && (
        <Html position={[room.x, room.height + 0.8, room.z]} center distanceFactor={48} zIndexRange={[15, 0]}>
          <div className="pointer-events-none select-none rounded bg-background/90 px-2 py-1 text-xs font-semibold shadow-md border border-border whitespace-nowrap">
            {room.shortName}
          </div>
        </Html>
      )}
    </group>
  );
}

// ── Hunt overlay beacon ─────────────────────────────────────────────────

const BEACON_STATUS_COLOR: Record<HuntOverlayStatus, string> = {
  live: '#f59e0b',
  upcoming: '#38bdf8',
  ended: '#94a3b8',
  unscheduled: '#64748b',
};

function HuntRoomBeaconMesh({
  beacon,
  isSelected,
  onSelect,
}: {
  beacon: HuntRoomBeacon;
  isSelected: boolean;
  onSelect: (item: ConferenceSelectedItem) => void;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const ringRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  const color = beacon.completed
    ? '#10b981'
    : (BEACON_STATUS_COLOR[beacon.status] ?? BEACON_STATUS_COLOR.live);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) {
      groupRef.current.position.y =
        beacon.y + (beacon.completed ? 0.08 : Math.sin(t * 2.4) * 0.28);
    }
    if (ringRef.current) {
      ringRef.current.rotation.z = beacon.completed ? 0 : t * 1.4;
      const pulse = beacon.completed ? 1 : 1 + Math.sin(t * 3.2) * 0.12;
      ringRef.current.scale.set(pulse, pulse, 1);
    }
    if (glowRef.current) {
      const mat = glowRef.current.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = beacon.completed
        ? 0.85
        : 0.55 + Math.sin(t * 4) * 0.25;
      glowRef.current.scale.setScalar(
        beacon.completed ? 1.05 : 1 + Math.sin(t * 2.6) * 0.08,
      );
    }
  });

  const toSelection = (): ConferenceSelectedItem => ({
    kind: 'room',
    roomId: beacon.roomId,
    stationId: beacon.stationId,
    stationName: beacon.sessionTitle || beacon.stationName || beacon.roomLabel,
    stationPoints: beacon.stationPoints,
    challenges: beacon.challenges,
    roomLabel: beacon.roomLabel,
    presenter: beacon.presenter,
    startTime: beacon.startTime,
    endTime: beacon.endTime,
    scheduleStatus: beacon.status,
    sessionTitle: beacon.sessionTitle,
  });

  const handleSelect = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    onSelect(toSelection());
  };

  return (
    <group position={[beacon.x, 0, beacon.z]}>
      <mesh
        position={[0, 0.12, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={handleSelect}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'default';
        }}
      >
        <circleGeometry args={[2.4, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={isSelected ? 0.55 : 0.28}
          transparent
          opacity={0.35}
          side={THREE.DoubleSide}
        />
      </mesh>

      <group ref={groupRef} position={[0, beacon.y, 0]}>
        <mesh position={[0, -0.9, 0]} castShadow onClick={handleSelect}>
          <cylinderGeometry args={[0.12, 0.18, 1.8, 12]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={0.45}
            metalness={0.2}
            roughness={0.35}
          />
        </mesh>

        <mesh ref={glowRef} castShadow onClick={handleSelect}>
          <sphereGeometry args={[0.55, 24, 24]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={0.7}
            metalness={0.15}
            roughness={0.25}
          />
        </mesh>

        <mesh ref={ringRef} rotation={[Math.PI / 2, 0, 0]} onClick={handleSelect}>
          <torusGeometry args={[0.95, 0.06, 10, 36]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive={color}
            emissiveIntensity={0.8}
            transparent
            opacity={0.85}
          />
        </mesh>

        <Html position={[0, 1.35, 0]} center distanceFactor={42} zIndexRange={[15, 0]}>
          <button
            type="button"
            className="pointer-events-auto select-none rounded-full border px-2.5 py-1 text-[11px] font-semibold shadow-lg backdrop-blur-sm whitespace-nowrap transition-transform hover:scale-105"
            style={{
              backgroundColor: 'color-mix(in oklab, var(--background) 88%, transparent)',
              borderColor: color,
              color: 'var(--foreground)',
              boxShadow: `0 0 18px ${color}55`,
            }}
            onClick={(ev) => {
              ev.stopPropagation();
              onSelect(toSelection());
            }}
          >
            <span
              className={
                beacon.completed
                  ? 'mr-1.5 inline-block h-1.5 w-1.5 rounded-full'
                  : 'mr-1.5 inline-block h-1.5 w-1.5 rounded-full animate-pulse'
              }
              style={{ backgroundColor: color }}
            />
            {beacon.shortLabel}
            {beacon.completed
              ? ' · DONE'
              : beacon.liveCount > 0
                ? ` · ${beacon.liveCount}`
                : ''}
          </button>
        </Html>
      </group>
    </group>
  );
}

// ── Booth helpers ───────────────────────────────────────────────────────

/** Yaw so booth frontage / signage faces the open aisle. */
function facingYaw(facing: BoothFacing): number {
  switch (facing) {
    case 'south':
      return 0;
    case 'north':
      return Math.PI;
    case 'east':
      return Math.PI / 2;
    case 'west':
      return -Math.PI / 2;
  }
}

/** Offset along the open-face direction (positive = toward aisle). */
function facingOffset(
  facing: BoothFacing,
  amount: number
): [number, number, number] {
  switch (facing) {
    case 'east':
      return [amount, 0, 0];
    case 'west':
      return [-amount, 0, 0];
    case 'south':
      return [0, 0, amount];
    case 'north':
      return [0, 0, -amount];
  }
}

function boothBackWall(
  facing: BoothFacing,
  width: number,
  depth: number
): { pos: [number, number, number]; size: [number, number, number] } {
  const wallT = 0.12;
  const wallH = 2.6;
  const wallY = wallH / 2 + 0.06;
  // Keep wall inside footprint so shallow pads (depth ~1.5) don't spill into aisles
  switch (facing) {
    case 'east':
      return {
        pos: [-width / 2 + wallT / 2, wallY, 0],
        size: [wallT, wallH, Math.max(depth - 0.08, 0.4)]
      };
    case 'west':
      return {
        pos: [width / 2 - wallT / 2, wallY, 0],
        size: [wallT, wallH, Math.max(depth - 0.08, 0.4)]
      };
    case 'south':
      return {
        pos: [0, wallY, -depth / 2 + wallT / 2],
        size: [Math.max(width - 0.08, 0.4), wallH, wallT]
      };
    case 'north':
      return {
        pos: [0, wallY, depth / 2 - wallT / 2],
        size: [Math.max(width - 0.08, 0.4), wallH, wallT]
      };
  }
}

function boothSideWalls(
  facing: BoothFacing,
  width: number,
  depth: number
): Array<{ pos: [number, number, number]; size: [number, number, number] }> {
  const wallT = 0.1;
  const wallH = 2.2;
  const wallY = wallH / 2 + 0.06;
  // Side walls leave the open face clear
  if (facing === 'east' || facing === 'west') {
    const span = Math.max(depth * 0.92, 0.35);
    if (depth < 2) {
      const xInset = (width / 2 - wallT / 2) * 0.35;
      return [
        { pos: [-xInset, wallY, 0], size: [wallT, wallH, span] },
        { pos: [xInset, wallY, 0], size: [wallT, wallH, span] }
      ];
    }
    return [
      {
        pos: [0, wallY, -depth / 2 + wallT / 2],
        size: [Math.max(width - 0.16, 0.3), wallH, wallT]
      },
      {
        pos: [0, wallY, depth / 2 - wallT / 2],
        size: [Math.max(width - 0.16, 0.3), wallH, wallT]
      }
    ];
  }
  // north / south open face — side walls on ±X
  const span = Math.max(depth * 0.9, 0.35);
  return [
    { pos: [-width / 2 + wallT / 2, wallY, 0], size: [wallT, wallH, span] },
    { pos: [width / 2 - wallT / 2, wallY, 0], size: [wallT, wallH, span] }
  ];
}

function boothCounter(
  facing: BoothFacing,
  width: number,
  depth: number
): { pos: [number, number, number]; size: [number, number, number] } {
  const counterH = 1.05;
  const counterY = counterH / 2 + 0.06;
  const thickness = Math.min(0.32, Math.max(Math.min(width, depth) * 0.22, 0.18));
  const inset = Math.min(0.28, Math.max(Math.min(width, depth) * 0.18, 0.12));

  switch (facing) {
    case 'east':
      return {
        pos: [width / 2 - inset, counterY, 0],
        size: [thickness, counterH, Math.max(depth * 0.62, 0.5)]
      };
    case 'west':
      return {
        pos: [-width / 2 + inset, counterY, 0],
        size: [thickness, counterH, Math.max(depth * 0.62, 0.5)]
      };
    case 'south':
      return {
        pos: [0, counterY, depth / 2 - inset],
        size: [Math.max(width * 0.7, 0.8), counterH, thickness]
      };
    case 'north':
      return {
        pos: [0, counterY, -depth / 2 + inset],
        size: [Math.max(width * 0.7, 0.8), counterH, thickness]
      };
  }
}

function boothRoofFrame(
  width: number,
  depth: number
): { pos: [number, number, number]; size: [number, number, number] } {
  // Double-height header banner gives longer exhibitor names enough vertical room to wrap.
  const beamW = Math.min(Math.max(width, depth) * 0.55, Math.max(width, 1.4));
  const beamD = Math.min(0.22, Math.max(Math.min(width, depth) * 0.12, 0.12));
  return { pos: [0, 3.85, 0], size: [beamW, 1.4, beamD] };
}

// ── Booth component ─────────────────────────────────────────────────────

function BoothMesh({
  booth,
  station,
  challenges,
  onSelect,
  isSelected
}: {
  booth: BoothDef;
  station?: PublicStation;
  challenges: PublicChallenge[];
  onSelect: (item: ConferenceSelectedItem) => void;
  isSelected: boolean;
}) {
  const { frame, roof, accent } = BOOTH_COLORS[booth.category];
  const pillarRef = useRef<THREE.Mesh>(null);
  const hasChallenges = challenges.length > 0;
  const back = boothBackWall(booth.facing, booth.width, booth.depth);
  const counter = boothCounter(booth.facing, booth.width, booth.depth);
  const sides = boothSideWalls(booth.facing, booth.width, booth.depth);
  const roofBeam = boothRoofFrame(booth.width, booth.depth);
  const exhibitorName = station ? parseExhibitorName(station.name) : undefined;
  const sponsorName = exhibitorName === 'Circle T Software Foundry'
    ? 'Sovereign Foundry'
    : exhibitorName ?? (booth.isAvailable ? 'Available' : `Stand ${booth.stand}`);
  const sponsorFontSize = Math.min(
    0.42,
    Math.max(0.18, roofBeam.size[0] * (sponsorName.length > 24 ? 0.13 : sponsorName.length > 16 ? 0.17 : 0.22))
  );
  const yaw = facingYaw(booth.facing);
  // Pull labels slightly into the aisle so they don't sit inside walls
  const labelLift = facingOffset(booth.facing, Math.min(booth.width, booth.depth) * 0.15 + 0.15);
  const minFoot = Math.min(booth.width, booth.depth);
  const pillarSize = Math.min(0.32, Math.max(minFoot * 0.18, 0.18));

  const handleSelect = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    onSelect({
      kind: 'booth',
      stand: booth.stand,
      stationId: station?.id,
      stationName: station?.name ?? (booth.isAvailable ? `Stand ${booth.stand} — Available` : `Stand ${booth.stand}`),
      stationPoints: station?.points ?? 0,
      challenges,
      roomLabel: station?.room ?? booth.location
    });
  };

  useFrame((state) => {
    if (pillarRef.current && isSelected) {
      const t = state.clock.elapsedTime;
      pillarRef.current.scale.y = 1 + Math.sin(t * 3) * 0.1;
    } else if (pillarRef.current) {
      pillarRef.current.scale.y = 1;
    }
  });

  const challengeBadges = useMemo(() => {
    const types = new Set(challenges.map((c) => c.type));
    return Array.from(types);
  }, [challenges]);

  const hatchLen = Math.max(Math.min(booth.width, booth.depth) * 0.9, 0.4);
  const hatchW = Math.max(Math.min(booth.width, booth.depth) * 0.14, 0.12);

  return (
    <group position={[booth.x, 0, booth.z]}>
      {/* Invisible full-pad hitbox — reliable selection on shallow booths */}
      <mesh
        position={[0, 1.2, 0]}
        onClick={handleSelect}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'default';
        }}
      >
        <boxGeometry args={[booth.width + 0.15, 2.6, booth.depth + 0.15]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Floor pad — hatched-booth look */}
      <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[booth.width, booth.depth]} />
        <meshStandardMaterial
          color={frame}
          roughness={0.65}
          emissive={isSelected ? accent : '#000000'}
          emissiveIntensity={isSelected ? 0.28 : 0}
        />
      </mesh>
      {/* Selection ring on pad */}
      {isSelected && (
        <mesh position={[0, 0.08, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry
            args={[
              Math.max(Math.min(booth.width, booth.depth) * 0.35, 0.35),
              Math.max(Math.min(booth.width, booth.depth) * 0.48, 0.5),
              32
            ]}
          />
          <meshBasicMaterial color={accent} transparent opacity={0.85} depthWrite={false} />
        </mesh>
      )}
      {/* Diagonal hatch overlay (scaled for shallow pads) */}
      <mesh position={[0, 0.07, 0]} rotation={[-Math.PI / 2, 0, Math.PI / 4]}>
        <planeGeometry args={[hatchLen, hatchW]} />
        <meshStandardMaterial color="#9a9488" transparent opacity={0.35} depthWrite={false} />
      </mesh>

      <mesh position={back.pos} castShadow receiveShadow>
        <boxGeometry args={back.size} />
        <meshStandardMaterial color={roof} roughness={0.7} />
      </mesh>
      {sides.map((side, i) => (
        <mesh key={i} position={side.pos} castShadow receiveShadow>
          <boxGeometry args={side.size} />
          <meshStandardMaterial color={roof} roughness={0.75} />
        </mesh>
      ))}
      <mesh position={counter.pos} castShadow>
        <boxGeometry args={counter.size} />
        <meshStandardMaterial color={accent} roughness={0.5} metalness={0.1} />
      </mesh>

      <mesh ref={pillarRef} position={[0, 1.7, 0]} castShadow>
        <boxGeometry args={[pillarSize, Math.min(3.4, 2.2 + minFoot * 0.4), pillarSize]} />
        <meshStandardMaterial
          color={accent}
          emissive={isSelected ? accent : '#000000'}
          emissiveIntensity={isSelected ? 0.5 : 0}
        />
      </mesh>

      {/* Sign board faces the open aisle */}
      <group position={[labelLift[0] * 0.4, 0, labelLift[2] * 0.4]} rotation={[0, yaw, 0]}>
        <mesh position={[0, roofBeam.pos[1], 0]} castShadow>
          <boxGeometry args={roofBeam.size} />
          <meshStandardMaterial color={frame} roughness={0.4} metalness={0.2} />
        </mesh>
        <Text
          position={[0, roofBeam.pos[1], roofBeam.size[2] / 2 + 0.02]}
          fontSize={sponsorFontSize}
          maxWidth={roofBeam.size[0] * 0.9}
          color="#1a1a1a"
          anchorX="center"
          anchorY="middle"
          textAlign="center"
        >
          {sponsorName}
        </Text>
        <Text
          position={[0, roofBeam.pos[1], -roofBeam.size[2] / 2 - 0.02]}
          rotation={[0, Math.PI, 0]}
          fontSize={sponsorFontSize}
          maxWidth={roofBeam.size[0] * 0.9}
          color="#1a1a1a"
          anchorX="center"
          anchorY="middle"
          textAlign="center"
        >
          {sponsorName}
        </Text>
      </group>

      {hasChallenges && (
        <mesh position={[labelLift[0], 5, labelLift[2]]}>
          <sphereGeometry args={[0.2, 12, 12]} />
          <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={0.6} />
        </mesh>
      )}

      {challengeBadges.map((type, i) => {
        const badgeSpread = Math.min(booth.width, 2.2);
        const x = -badgeSpread / 2 + 0.35 + i * Math.min(0.45, badgeSpread / Math.max(challengeBadges.length, 1));
        return (
          <mesh key={type} position={[x + labelLift[0] * 0.3, 5, labelLift[2] * 0.3]}>
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshStandardMaterial
              color={
                String(type) === 'cv_scan'
                  ? '#8b5cf6'
                  : String(type) === 'quiz'
                    ? '#3b82f6'
                    : String(type) === 'ar_view'
                      ? '#f59e0b'
                      : '#64748b'
              }
              emissive={
                String(type) === 'cv_scan'
                  ? '#8b5cf6'
                  : String(type) === 'quiz'
                    ? '#3b82f6'
                    : String(type) === 'ar_view'
                      ? '#f59e0b'
                      : '#64748b'
              }
              emissiveIntensity={0.4}
            />
          </mesh>
        );
      })}

    </group>
  );
}

// ── Registration desk ────────────────────────────────────────────────────

function RegistrationDesk({
  station,
  challenges,
  onSelect,
  isSelected
}: {
  station?: PublicStation;
  challenges: PublicChallenge[];
  onSelect: (item: ConferenceSelectedItem) => void;
  isSelected: boolean;
}) {
  const { x, z, width, depth, height, color, label } = REGISTRATION_DESK;
  return (
    <group position={[x, 0, z]}>
      <mesh
        position={[0, height / 2, 0]}
        castShadow
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onSelect({
            kind: 'registration',
            stationId: station?.id,
            stationName: station?.name ?? 'Registration Desk',
            stationPoints: station?.points ?? 0,
            challenges
          });
        }}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'default';
        }}
      >
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial
          color={color}
          emissive={isSelected ? color : '#000000'}
          emissiveIntensity={isSelected ? 0.35 : 0}
        />
      </mesh>
      <Text
        position={[width / 2 + 0.05, height + 0.4, 0]}
        rotation={[0, -Math.PI / 2, 0]}
        fontSize={0.4}
        color="#4b5563"
        anchorX="center"
        anchorY="middle"
      >
        {label}
      </Text>
    </group>
  );
}

function RestroomBlock() {
  const { x, z, width, depth, height, color, wallColor } = RESTROOM_BLOCK;
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[width, depth]} />
        <meshStandardMaterial color={color} roughness={0.9} />
      </mesh>
      <mesh position={[0, height / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[width * 0.92, height, depth * 0.92]} />
        <meshStandardMaterial color={wallColor} transparent opacity={0.55} roughness={0.8} />
      </mesh>
      {/* Simple partition into two cubicles */}
      <mesh position={[0, height / 2, 0]} castShadow>
        <boxGeometry args={[0.15, height * 0.9, depth * 0.85]} />
        <meshStandardMaterial color="#a89f94" />
      </mesh>
      <Html position={[0, height + 0.6, 0]} center distanceFactor={50} zIndexRange={[15, 0]}>
        <div className="pointer-events-none select-none rounded bg-background/90 px-2 py-0.5 text-[10px] font-medium shadow border border-border whitespace-nowrap">
          Restrooms
        </div>
      </Html>
    </group>
  );
}

function StructuralWalls() {
  const geos = useMemo(
    () =>
      STRUCTURAL_WALLS.map((w: WallSegment) =>
        createWallGeometry(w.x1, w.z1, w.x2, w.z2, w.height ?? 3.2, w.thickness ?? 0.28)
      ),
    []
  );
  return (
    <group>
      {geos.map((geo, i) => (
        <mesh key={i} geometry={geo} castShadow receiveShadow>
          <meshStandardMaterial
            color="#c4b8a4"
            transparent
            opacity={0.4}
            roughness={0.85}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

// ── Camera controller ───────────────────────────────────────────────────
// Preset buttons animate once toward the target view, then release full
// OrbitControls (rotate / pan / zoom) to the user. Continuous lerping was
// fighting drag input and made controls feel broken.

/** Minimal OrbitControls surface used by the camera controller. */
type OrbitControlsHandle = {
  object: THREE.Camera & { position: THREE.Vector3 };
  target: THREE.Vector3;
  update: () => void;
  enabled: boolean;
};

function CameraController({ preset, cameraRef, zoomTarget }: { preset: CameraPreset; cameraRef: ForwardedRef<ConferenceTwinCameraController>; zoomTarget: ZoomTarget | null }) {
  const controlsRef = useRef<OrbitControlsHandle | null>(null);
  const animatingRef = useRef(false);
  const lastPresetId = useRef<string | null>(null);
  const fromPos = useRef(new THREE.Vector3());
  const fromTarget = useRef(new THREE.Vector3());
  const toPos = useRef(new THREE.Vector3(...preset.position));
  const toTarget = useRef(new THREE.Vector3(...preset.target));
  const progress = useRef(1);
  const presetRef = useRef(preset);
  presetRef.current = preset;

  const animateTo = (position: THREE.Vector3, target: THREE.Vector3) => {
    const controls = controlsRef.current;
    if (!controls) return;
    fromPos.current.copy(controls.object.position);
    fromTarget.current.copy(controls.target);
    toPos.current.copy(position);
    toTarget.current.copy(target);
    progress.current = 0;
    animatingRef.current = true;
  };

  useImperativeHandle(cameraRef, () => ({
    zoomIn: () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const offset = controls.object.position.clone().sub(controls.target);
      const distance = THREE.MathUtils.clamp(offset.length() * 0.8, 10, 200);
      animateTo(controls.target.clone().addScaledVector(offset.normalize(), distance), controls.target.clone());
    },
    zoomOut: () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const offset = controls.object.position.clone().sub(controls.target);
      const distance = THREE.MathUtils.clamp(offset.length() * 1.25, 10, 200);
      animateTo(controls.target.clone().addScaledVector(offset.normalize(), distance), controls.target.clone());
    },
    pan: (direction) => {
      const controls = controlsRef.current;
      if (!controls) return;
      const distance = controls.object.position.distanceTo(controls.target);
      const forward = controls.target.clone().sub(controls.object.position).setY(0).normalize();
      const right = new THREE.Vector3().setFromMatrixColumn(controls.object.matrixWorld, 0).setY(0).normalize();
      const offset = direction === 'up' ? forward : direction === 'down' ? forward.negate() : direction === 'left' ? right.negate() : right;
      offset.multiplyScalar(Math.max(distance * 0.1, 4));
      const target = controls.target.clone().add(offset);
      target.x = THREE.MathUtils.clamp(target.x, FLOOR_BOUNDS.minX, FLOOR_BOUNDS.maxX);
      target.z = THREE.MathUtils.clamp(target.z, FLOOR_BOUNDS.minZ, FLOOR_BOUNDS.maxZ);
      animateTo(controls.object.position.clone().add(target.clone().sub(controls.target)), target);
    },
    reset: () => {
      const activePreset = presetRef.current;
      animateTo(new THREE.Vector3(...activePreset.position), new THREE.Vector3(...activePreset.target));
    },
  }));

  // When the user picks a camera chip, start a one-shot ease (does not run every frame forever)
  useEffect(() => {
    if (!zoomTarget) return;
    const controls = controlsRef.current;
    if (!controls) return;

    const target = new THREE.Vector3(
      THREE.MathUtils.clamp(zoomTarget[0], FLOOR_BOUNDS.minX, FLOOR_BOUNDS.maxX),
      THREE.MathUtils.clamp(zoomTarget[1], 0, 12),
      THREE.MathUtils.clamp(zoomTarget[2], FLOOR_BOUNDS.minZ, FLOOR_BOUNDS.maxZ),
    );
    const offset = controls.object.position.clone().sub(controls.target);
    const distance = THREE.MathUtils.clamp(offset.length() * 0.65, 10, 200);
    animateTo(target.clone().addScaledVector(offset.normalize(), distance), target);
  }, [zoomTarget]);

  useEffect(() => {
    toPos.current.set(...preset.position);
    toTarget.current.set(...preset.target);

    const controls = controlsRef.current;
    if (!controls || lastPresetId.current === null) {
      // Controls not ready yet, or first paint — useFrame will snap once
      return;
    }
    if (lastPresetId.current === preset.id) return;

    fromPos.current.copy(controls.object.position);
    fromTarget.current.copy(controls.target);
    progress.current = 0;
    animatingRef.current = true;
    lastPresetId.current = preset.id;
  }, [preset.id, preset.position, preset.target]);

  useFrame((_, delta) => {
    const controls = controlsRef.current;
    if (!controls) return;

    // First time controls exist: snap to the active preset, then free the user
    if (lastPresetId.current === null) {
      const p = presetRef.current;
      controls.object.position.set(...p.position);
      controls.target.set(...p.target);
      controls.update();
      controls.enabled = true;
      lastPresetId.current = p.id;
      animatingRef.current = false;
      progress.current = 1;
      return;
    }

    if (!animatingRef.current) {
      controls.enabled = true;
      return;
    }

    controls.enabled = false;
    progress.current = Math.min(1, progress.current + delta * 1.6);
    const t = 1 - Math.pow(1 - progress.current, 3);
    controls.object.position.lerpVectors(fromPos.current, toPos.current, t);
    controls.target.lerpVectors(fromTarget.current, toTarget.current, t);
    controls.update();

    if (progress.current >= 1) {
      animatingRef.current = false;
      controls.enabled = true;
    }
  });

  return (
    <OrbitControls
      ref={controlsRef as never}
      makeDefault
      enableDamping
      dampingFactor={0.08}
      minPolarAngle={0.08}
      maxPolarAngle={Math.PI / 2.05}
      minDistance={10}
      maxDistance={200}
      enablePan
      enableZoom
      enableRotate
      screenSpacePanning
      panSpeed={0.9}
      rotateSpeed={0.7}
      zoomSpeed={1.05}
      target={preset.target}
    />
  );
}

// ── Main scene ───────────────────────────────────────────────────────────

export const ConferenceTwinScene = forwardRef<ConferenceTwinCameraController, ConferenceTwinSceneProps>(function ConferenceTwinScene({
  stations,
  challenges,
  onSelect,
  selectedId,
  cameraPreset,
  huntOverlayEnabled = false,
  huntBeacons = [],
  virtualScreenContent = {},
  wallItems = [],
  selectedWallItemId = null,
  onWallItemSelect,
  children
}: ConferenceTwinSceneProps, cameraRef) {
  // Registration = sortOrder 0; room sort orders begin at 11. Booth stations are
  // resolved by their explicit “Stand N — …” name so the two sets never collide.
  const stationBySort = useMemo(() => {
    const map = new Map<number, PublicStation>();
    for (const s of stations) {
      map.set(s.sortOrder, s);
    }
    return map;
  }, [stations]);

  const roomStations = useMemo(() => {
    const result: { room: RoomDef; station?: PublicStation; challenges: PublicChallenge[] }[] = [];
    ROOMS.forEach((room, i) => {
      const sortOrder = 100 + i;
      const station = stationBySort.get(sortOrder);
      const roomChallenges = station
        ? challenges.filter((c) => c.stationId === station.id)
        : [];
      result.push({ room, station, challenges: roomChallenges });
    });
    return result;
  }, [stationBySort, challenges]);

  const boothData = useMemo(() => {
    return BOOTHS.map((booth) => {
      const station = stations.find((s) => parseStandNumber(s.name) === booth.stand);
      const boothChallenges = station
        ? challenges.filter((c) => c.stationId === station.id)
        : [];
      return { booth, station, challenges: boothChallenges };
    });
  }, [stations, challenges]);

  const wallPlaques = useMemo(() => {
    const groups = new Map<string, EventTwinItem[]>();
    for (const item of wallItems) {
      if (!item.photoUrl) continue;
      const key = `${item.wallAnchor.roomId}:${item.wallAnchor.wallId}`;
      groups.set(key, [...(groups.get(key) ?? []), item]);
    }
    return Array.from(groups.values()).flatMap((items) =>
      items.map((item, index) => ({
        item,
        tangentOffset: (index - (items.length - 1) / 2) * 2.65,
      })),
    );
  }, [wallItems]);

  const regStation = stationBySort.get(0);
  const regChallenges = regStation
    ? challenges.filter((c) => c.stationId === regStation.id)
    : [];

  const floorCenterX = FLOOR_BOUNDS.centerX;
  const floorCenterZ = FLOOR_BOUNDS.centerZ;
  const [zoomTarget, setZoomTarget] = useState<ZoomTarget | null>(null);

  const zoomTowardPoint = (point: THREE.Vector3) => {
    setZoomTarget([point.x, point.y, point.z]);
  };

  return (
    <Canvas
      shadows
      className="touch-none"
      style={{ width: '100%', height: '100%', touchAction: 'none' }}
      camera={{ position: cameraPreset.position, fov: 45, near: 0.1, far: 1200 }}
      onPointerMissed={() => onSelect(null)}
      gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
      dpr={[1, 2]}
    >
      <ClickToZoom onZoom={zoomTowardPoint} />
      <ambientLight intensity={0.72} />
      <directionalLight
        position={[40, 70, 25]}
        intensity={1.05}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-70}
        shadow-camera-right={70}
        shadow-camera-top={70}
        shadow-camera-bottom={-70}
      />
      <hemisphereLight args={['#e0e7ff', '#4b5563', 0.32]} />
      <color attach="background" args={['#1e293b']} />

      {/* Composite Level 3 floor plates */}
      {FLOOR_PLATES.map((plate, i) => (
        <mesh
          key={i}
          position={[plate.x + plate.width / 2, 0, plate.z + plate.depth / 2]}
          rotation={[-Math.PI / 2, 0, 0]}
          receiveShadow
        >
          <planeGeometry args={[plate.width, plate.depth]} />
          <meshStandardMaterial color={plate.floorColor} roughness={0.95} />
        </mesh>
      ))}

      {/* Soft outer skirt so the footprint reads clearly */}
      <mesh
        position={[floorCenterX, -0.04, floorCenterZ]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
      >
        <planeGeometry args={[130, 90]} />
        <meshStandardMaterial color="#0f172a" roughness={1} />
      </mesh>

      <StructuralWalls />

      {AISLES.map((aisle, i) => (
        <mesh
          key={i}
          position={[aisle.x, 0.03, aisle.z]}
          rotation={[-Math.PI / 2, 0, 0]}
          receiveShadow
        >
          <planeGeometry args={[aisle.width, aisle.depth]} />
          <meshStandardMaterial color={aisle.color} roughness={0.9} />
        </mesh>
      ))}

      {/* Sponsors Space label */}
      <Html position={[-2, 0.2, 0]} center distanceFactor={55} zIndexRange={[15, 0]}>
        <div className="pointer-events-none select-none rounded bg-background/80 px-2 py-1 text-[11px] font-semibold text-muted-foreground shadow border border-border/60 whitespace-nowrap">
          Sponsors Space
        </div>
      </Html>

      {roomStations.map(({ room, station, challenges: roomChallenges }) => (
        <RoomMesh
          key={room.id}
          room={room}
          stationId={station?.id}
          challenges={roomChallenges}
          virtualScreenContent={virtualScreenContent[room.id]}
          hasHuntBeacon={
            huntOverlayEnabled &&
            huntBeacons.some(
              (beacon) =>
                beacon.roomId === room.id || beacon.stationId === station?.id,
            )
          }
          onSelect={onSelect}
          isSelected={
            selectedId === station?.id ||
            selectedId === room.id ||
            (huntOverlayEnabled &&
              huntBeacons.some(
                (b) =>
                  (b.roomId === room.id || b.stationId === station?.id) &&
                  (selectedId === b.id ||
                    selectedId === b.roomId ||
                    selectedId === b.stationId),
              ))
          }
        />
      ))}

      {huntOverlayEnabled &&
        huntBeacons.map((beacon) => (
          <HuntRoomBeaconMesh
            key={beacon.id}
            beacon={beacon}
            isSelected={
              selectedId === beacon.id ||
              selectedId === beacon.roomId ||
              selectedId === beacon.stationId
            }
            onSelect={onSelect}
          />
        ))}

      {boothData.map(({ booth, station, challenges: boothChallenges }) => (
        <BoothMesh
          key={booth.stand}
          booth={booth}
          station={station}
          challenges={boothChallenges}
          onSelect={onSelect}
          isSelected={
            selectedId === station?.id ||
            selectedId === `stand-${booth.stand}`
          }
        />
      ))}

      {onWallItemSelect && wallPlaques.map(({ item, tangentOffset }) => (
        <Suspense key={item.id} fallback={null}>
          <WallPlaqueMesh
            item={item}
            selected={selectedWallItemId === item.id}
            wallTangentOffset={tangentOffset}
            onSelect={onWallItemSelect}
          />
        </Suspense>
      ))}

      <RegistrationDesk
        station={regStation}
        challenges={regChallenges}
        onSelect={onSelect}
        isSelected={selectedId === regStation?.id}
      />

      <RestroomBlock />

      {/* Hotel reception corridor cue */}
      <mesh
        position={[HOTEL_RECEPTION.x, 0.035, HOTEL_RECEPTION.z]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
      >
        <planeGeometry args={[HOTEL_RECEPTION.width, HOTEL_RECEPTION.depth]} />
        <meshStandardMaterial color="#efe9df" roughness={0.92} />
      </mesh>
      <Html position={[HOTEL_RECEPTION.x + 4, 1.5, HOTEL_RECEPTION.z]} center distanceFactor={50} zIndexRange={[15, 0]}>
        <div className="pointer-events-none select-none rounded bg-background/90 px-2 py-1 text-xs font-semibold shadow border border-border whitespace-nowrap">
          {HOTEL_RECEPTION.label}
        </div>
      </Html>

      {/* Injected children (e.g. PedestrianSimulation) */}
      {children}

      <Billboard position={[floorCenterX - 4, 9, FLOOR_BOUNDS.minZ + 4]}>
        <Text fontSize={1.1} color="#818cf8" anchorX="center" anchorY="middle">
          Conference Centre · Level 3
        </Text>
      </Billboard>

      <CameraController preset={cameraPreset} cameraRef={cameraRef} zoomTarget={zoomTarget} />
    </Canvas>
  );
});

// ── WebGL detection (re-export) ──────────────────────────────────────────

export function isWebGLAvailable(): boolean {
  return checkWebGLCapability();
}

// ── Legend component ──────────────────────────────────────────────────────

export function ConferenceTwinLegend({
  huntOverlayEnabled = false,
  fullScreenLandscape = false,
}: {
  huntOverlayEnabled?: boolean;
  fullScreenLandscape?: boolean;
}) {
  const items = [
    { label: 'Platinum sponsor', color: '#e5e4e2', dot: '#6366f1' },
    { label: 'Gold sponsor', color: '#ffd700', dot: '#f59e0b' },
    { label: 'Silver sponsor', color: '#c0c0c0', dot: '#6b7280' },
    { label: 'Standard booth', color: '#3b82f6', dot: '#60a5fa' },
    { label: 'Has challenges', color: '#22c55e', dot: '#22c55e' },
    { label: 'CV scan target', color: '#8b5cf6', dot: '#8b5cf6' },
    { label: 'Virtual screens', color: '#38bdf8', dot: '#38bdf8' },
    ...(huntOverlayEnabled
      ? [
          { label: 'Live session beacon', color: '#f59e0b', dot: '#f59e0b' },
          { label: 'Upcoming session', color: '#38bdf8', dot: '#38bdf8' },
        ]
      : []),
  ];

  const legend = (
    <div className="space-y-2 text-xs">
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-2">
            <span className="inline-block size-3 rounded-full" style={{ backgroundColor: item.dot }} />
            <span className="text-muted-foreground">{item.label}</span>
          </div>
        ))}
      </div>
      <p className="pt-1 text-[10px] leading-snug text-muted-foreground/80">
        Drag to rotate · Scroll to zoom · Right-drag / two-finger to pan
        {huntOverlayEnabled ? ' · Tap a beacon or screen for session details' : ' · Tap a screen for session details'}
      </p>
    </div>
  );

  return (
    <>
      <DesktopTwinPanel
        title={huntOverlayEnabled ? 'Hunt overlay' : 'Conference legend'}
        icon={ListIcon}
        className={fullScreenLandscape ? 'left-4 top-16 z-10' : 'left-4 top-4 z-10'}
        panelClassName="w-[200px]"
        contentClassName="p-1"
      >
        {legend}
      </DesktopTwinPanel>
      <Sheet>
        <div className={fullScreenLandscape ? 'absolute left-3 top-16 z-30 sm:hidden' : 'absolute left-3 top-3 z-20 sm:hidden'} onPointerDown={(event) => event.stopPropagation()}>
          <SheetTrigger asChild>
            <Button type="button" size="icon" variant="secondary" className="size-11 rounded-xl border bg-card/95 shadow-lg backdrop-blur" aria-label="Open conference legend">
              <ListIcon className="size-5" aria-hidden />
            </Button>
          </SheetTrigger>
        </div>
        <SheetContent side="bottom" className={fullScreenLandscape ? 'max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:hidden' : 'max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:hidden'} onPointerDown={(event) => event.stopPropagation()}>
          <SheetHeader>
            <SheetTitle>{huntOverlayEnabled ? 'Hunt overlay legend' : 'Conference legend'}</SheetTitle>
            <SheetDescription>Map markers and navigation guidance for the conference twin.</SheetDescription>
          </SheetHeader>
          <div className="mt-5">{legend}</div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export { CAMERA_PRESETS };
